'use client';

import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

const floatingCards = [
  {
    icon: 'MusicalNoteIcon' as const,
    label: 'Singer',
    sub: 'Classical & Contemporary',
    delay: '0s',
    position: 'top-[12%] left-[2%] lg:left-[-2%]',
    rotate: '-2deg',
  },
  {
    icon: 'PencilSquareIcon' as const,
    label: 'Writer',
    sub: 'Poetry & Prose',
    delay: '1.5s',
    position: 'top-[55%] left-[1%] lg:left-[-4%]',
    rotate: '1.5deg',
  },
  {
    icon: 'SparklesIcon' as const,
    label: 'Coach',
    sub: 'Personality Development',
    delay: '0.8s',
    position: 'top-[10%] right-[2%] lg:right-[-2%]',
    rotate: '2deg',
  },
  {
    icon: 'SpeakerWaveIcon' as const,
    label: 'Musician',
    sub: 'Sitar & Harmonium',
    delay: '2s',
    position: 'bottom-[18%] right-[2%] lg:right-[-3%]',
    rotate: '-1.5deg',
  },
];

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background gradient */}
      <div
        className="absolute inset-0 z-0"
        style={{
          background:
            'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(201,169,154,0.18) 0%, rgba(184,150,110,0.08) 40%, transparent 70%), linear-gradient(180deg, #FAF6F0 0%, #F5EDE4 100%)',
        }}
      />

      {/* Decorative circles */}
      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full opacity-[0.04] z-0"
        style={{ border: '1px solid var(--gold)' }}
      />

      <div
        className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] rounded-full opacity-[0.02] z-0"
        style={{ border: '1px solid var(--gold)' }}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6">
        {/* MAIN GRID */}
        <div className="grid lg:grid-cols-2 gap-16 items-center min-h-[calc(100vh-80px)]">
          {/* LEFT TEXT */}
          <div className="flex flex-col gap-6 order-2 lg:order-1">
            <span className="section-label">Artist · Coach · Creator</span>

            <h1
              className="font-fraunces font-light leading-[0.9] tracking-tight"
              style={{ fontSize: 'clamp(3rem, 6vw, 5.5rem)' }}
            >
              Where <br />
              <em className="text-gold-shimmer not-italic">Art</em> meets <br />
              <em className="italic">Soul.</em>
            </h1>

            <p className="text-muted text-sm leading-relaxed max-w-md">
              Kavita Thakur — Singer, Writer, Musician, and Personality Development Coach helping
              you find your voice, on stage and in life.
            </p>

            {/* Buttons */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => {
                  document.getElementById('work')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-primary"
              >
                Explore My Work
                <Icon name="ArrowRightIcon" size={14} />
              </button>

              <button
                onClick={() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="btn-outline"
              >
                Book Session
              </button>
            </div>

            {/* Stats */}
            <div className="flex gap-10 pt-4">
              {[
                { num: '500+', label: 'Lives Coached' },
                { num: '12', label: 'Years on Stage' },
                { num: '3', label: 'Books Published' },
              ].map((s) => (
                <div key={s.label}>
                  <p className="font-fraunces text-2xl font-light text-charcoal">{s.num}</p>
                  <p className="text-10 tracking-widest uppercase text-muted mt-1">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* IMAGE SIDE */}
          <div className="order-1 lg:order-2 flex justify-center relative">
            {/* Floating cards */}
            {floatingCards.map((card, i) => (
              <div
                key={card.label}
                className={`absolute z-20 glass-card rounded-2xl px-4 py-3 flex items-center gap-3 ${card.position} hidden lg:flex`}
                style={{
                  transform: `rotate(${card.rotate})`,
                  animation: `float-slow ${6 + i * 0.8}s ease-in-out infinite ${card.delay}`,
                  minWidth: '168px',
                }}
              >
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{
                    background: 'linear-gradient(135deg, var(--gold) 0%, var(--gold-light) 100%)',
                  }}
                >
                  <Icon name={card.icon} size={18} className="text-white" variant="solid" />
                </div>

                <div>
                  <p className="font-semibold text-charcoal text-sm leading-none">{card.label}</p>
                  <p className="text-10 text-muted mt-0.5 tracking-wide">{card.sub}</p>
                </div>
              </div>
            ))}

            {/* Portrait */}
            <div className="relative w-full max-w-[420px] mx-auto">
              <div
                className="relative rounded-[40px] overflow-hidden img-hover-scale"
                style={{
                  aspectRatio: '4/5',
                  boxShadow: '0 32px 80px rgba(28,25,23,0.18), 0 8px 24px rgba(184,150,110,0.15)',
                }}
              >
                <AppImage
                  src="/assets/images/kavita-portrait.png"
                  alt="Kavita Thakur portrait"
                  fill
                  priority
                  className="object-cover"
                  sizes="(max-width: 768px) 100vw, 420px"
                />

                <div
                  className="absolute inset-0"
                  style={{
                    background: 'linear-gradient(to top, rgba(28,25,23,0.45) 0%, transparent 50%)',
                  }}
                />

                {/* Name badge */}
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="glass-card-dark rounded-2xl px-5 py-3">
                    <p className="font-fraunces text-xl italic text-cream font-light">
                      Kavita Thakur
                    </p>
                    <p className="text-10 tracking-widest uppercase text-cream/60 mt-1">
                      Pune · India
                    </p>
                  </div>
                </div>
              </div>

              {/* Decorations */}
              <div
                className="absolute -bottom-6 -right-6 w-32 h-32 rounded-full opacity-20 z-0"
                style={{ border: '1px solid var(--gold)' }}
              />

              <div
                className="absolute -top-4 -left-4 w-20 h-20 rounded-full opacity-15 z-0"
                style={{ background: 'var(--rose-muted)' }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-3">
        <span className="text-10 tracking-widest uppercase text-muted opacity-60">Scroll</span>

        <div className="scroll-line-container">
          <div className="scroll-line-inner" style={{ background: 'var(--gold)' }} />
        </div>
      </div>
    </section>
  );
}
